import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "./client";
import type { DayOfWeek, LocationRole } from "./types";
import { meQueryKey } from "./auth";

// Správa: uživatelé, oddělení a nastavení provozovny (M1).

const usersKey = ["users"] as const;
const departmentsKey = ["departments"] as const;
const locationKey = ["location"] as const;

export type UserInput = {
  displayName: string;
  email: string | null;
  role: LocationRole;
  departmentIds: string[];
  language: string;
};

export function useUsers(enabled = true) {
  return useQuery({ queryKey: usersKey, enabled, queryFn: async () => unwrap(await api.GET("/api/v1/users")) });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: UserInput & { userName: string }) => unwrap(await api.POST("/api/v1/users", { body })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: usersKey }),
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: UserInput & { id: string }) =>
      unwrap(await api.PUT("/api/v1/users/{id}", { params: { path: { id } }, body })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: usersKey }),
  });
}

export function useSetUserActive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) =>
      unwrap(
        active
          ? await api.POST("/api/v1/users/{id}/activate", { params: { path: { id } } })
          : await api.POST("/api/v1/users/{id}/deactivate", { params: { path: { id } } }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: usersKey }),
  });
}

export function useCreatePasswordLink() {
  return useMutation({
    mutationFn: async (id: string) =>
      unwrap(await api.POST("/api/v1/users/{id}/password-link", { params: { path: { id } } })),
  });
}

export function useDepartments(includeArchived = false) {
  return useQuery({
    queryKey: [...departmentsKey, { includeArchived }],
    queryFn: async () => unwrap(await api.GET("/api/v1/departments", { params: { query: { includeArchived } } })),
  });
}

function useDepartmentMutation<T>(fn: (input: T) => Promise<unknown>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: departmentsKey });
      void queryClient.invalidateQueries({ queryKey: meQueryKey });
    },
  });
}

export const useCreateDepartment = () =>
  useDepartmentMutation(async (name: string) => unwrap(await api.POST("/api/v1/departments", { body: { name } })));

export const useRenameDepartment = () =>
  useDepartmentMutation(async ({ id, name }: { id: string; name: string }) =>
    unwrap(await api.PUT("/api/v1/departments/{id}", { params: { path: { id } }, body: { name } })),
  );

export const useSetDepartmentArchived = () =>
  useDepartmentMutation(async ({ id, archived }: { id: string; archived: boolean }) =>
    unwrap(
      archived
        ? await api.POST("/api/v1/departments/{id}/archive", { params: { path: { id } } })
        : await api.POST("/api/v1/departments/{id}/restore", { params: { path: { id } } }),
    ),
  );

export const useReorderDepartments = () =>
  useDepartmentMutation(async (ids: string[]) => unwrap(await api.PUT("/api/v1/departments/order", { body: { ids } })));

export function useLocationSettings() {
  return useQuery({ queryKey: locationKey, queryFn: async () => unwrap(await api.GET("/api/v1/location")) });
}

export function useUpdateLocationSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      organizationName: string;
      name: string;
      timeZone: string;
      dayStart: string;
      openWeekdays: DayOfWeek[];
    }) => unwrap(await api.PUT("/api/v1/location", { body })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: locationKey });
      void queryClient.invalidateQueries({ queryKey: meQueryKey });
    },
  });
}
